/* global $fetch */
import { AsyncData, useAsyncData, useRuntimeConfig } from 'nuxt/app'
import type { ExtractedRouteMethod, TypedInternalResponse } from 'nitropack'
import { _AsyncData } from '#app/composables/asyncData'
import { TRedirect } from '~/types/response-types'
import { TNestedCollectionData, TPage, TPageRequestOptions } from '~/types/page-types'
import { useUserStore } from '~/store/user'
import { TGlobalSettings, TNavigationSettings, TSeoSettings } from '~/types/singleton-types'
import { TBlock } from '~/types/block-types'
import { TGridBlockFilter } from '~/types/grid-block-types'

// attempt to provide an auth header, if there's a user stored
const addUser = () => {
  const userStore = useUserStore()
  const auth = userStore.getAuth

  const extraHeaders: any = {}
  if (auth) {
    extraHeaders.Authorization = `Bearer ${auth.access_token}`
  }

  return extraHeaders
}

export const getPage = async (options: TPageRequestOptions
): Promise<AsyncData<TRedirect | TPage, Error | null>> => {
  const queryString = new URLSearchParams(options).toString()
  const extraHeaders = addUser()
  return await useAPIGet(`/page/?${queryString}`, extraHeaders)
}

/*
  get sorted grid data (for GridBlock).
*/
export const getSortedPageCards = async (variant: string, filters?: TGridBlockFilter[], self?: TGridBlockFilter, limit?: number, onHome?: boolean): Promise<AsyncData<object, Error | null>> => {
  const searchParams = new URLSearchParams()

  searchParams.append('variant', JSON.stringify(variant))

  if (filters) {
    searchParams.append('filters', JSON.stringify(filters))
  }

  if (self) {
    searchParams.append('self', JSON.stringify(self))
  }

  if (limit) {
    searchParams.append('limit', limit + '')
  }

  if (onHome) {
    searchParams.append('home', onHome + '')
  }

  const extraHeaders = addUser()

  return await useAPIGet(`/sorted-page-cards/?${searchParams.toString()}`, extraHeaders)
}

/*
  get data from a collection.
*/
export const getCollection = async (collection: string, filter?: object, fields?: string[], sort?: string[], limit = -1, page?: number, meta?: boolean): Promise<AsyncData<TNestedCollectionData, Error | null>> => {
  const queryString = new URLSearchParams()

  // filter as an object
  if (filter) {
    queryString.append('filter', JSON.stringify(filter))
  }

  // fields as a comma seperated string. (because...)
  if (fields) {
    queryString.append('fields', fields.join(','))
  }

  // sort as a comma seperated string.
  if (sort) {
    queryString.append('sort', sort.join(','))
  }

  // used for pagination
  if (limit) {
    queryString.append('limit', limit + '')
  }

  if (page) {
    queryString.append('page', page + '')
  }

  // get meta, to get the total length
  if (meta) {
    queryString.append('meta', 'filter_count')
  }

  const extraHeaders = addUser()
  return await useAPIGet(`/items/${collection}/?${queryString}`, extraHeaders)
}

/*
  search using elasticSearch connection established by the backend.
*/
export const getSearch = (query: string): Promise<any> => {
  const params = { query }
  const queryString = new URLSearchParams(params).toString()

  const extraHeaders = addUser()

  return useAPIGet(`/search/?${queryString}`, extraHeaders)
}

export const postForm = async (type: 'form' | 'newsletter', formId: string, data: FormData) => {
  const config = useRuntimeConfig()
  const baseUrl = (type === 'form') ? config.public.formSubmissionUrl : config.public.newsletterSubmissionUrl
  const url = baseUrl.replace('[ID]', formId)

  return await useAPIPost(url, data)
}

/*
  get navigation settings,
  also deals with nested menu structures
  (uses server/api/navigation-settings, which is cached for 5 minutes)
*/
export const getNavigation = (): AsyncData<TNavigationSettings,
    Error | null> => {
  return useAPIGet('/navigation-settings', null, true, true)
}

/*
  get global settings
  (uses server/api/global-config, which is cached for 5 minutes)
*/
export const getGlobalSettings = (): AsyncData<TGlobalSettings,
    Error | null> => {
  return useAPIGet('/global-config', null, true, true)
}

/*
  get SEO settings
  (uses server/api/seo-settings, which is cached for 5 minutes)
*/
export const getSeoSettings = (): AsyncData<TSeoSettings,
    Error | null> => {
  return useAPIGet('/seo-settings', null, true, true)
}

export const getBlock = (collection: string, id: number, fields = ['*.*.*']): AsyncData<TBlock,
    Error | null> => {
  const queryString = new URLSearchParams({ fields }).toString()
  return useAPIGet(`/items/${collection}/${id}?${queryString}`)
}

// get an image based on a generated uri (this uses server/api/thumbnail instead of a direct backend fetch)
export const getThumbnail = (uri: string): AsyncData<any,
    Error | null> => {
  return useAPIGet(`/thumbnail/${uri}`, null, true, true)
}

// get an image based on a generated uri (this uses server/api/thumbnail instead of a direct backend fetch)
export const setRendition = (uri: string): AsyncData<any,
    Error | null> => {
  return useAPIGet(`/rendition/${uri}`, null, true, true)
}

export const updateItem = async (collection: string, id: number, data: any) => {
  const extraHeaders = addUser()
  return await useAPIPatch(`/items/${collection}/${id}`, data, extraHeaders, true, false)
}

// Default GET method
const useAPIGet = (
  url: string,
  extraHeaders: any = null,
  server = true,
  frontendApi = false
): AsyncData<any, Error | null> => {
  const config = useRuntimeConfig()

  // frontend API is a nuxt3 server endpoint.
  const apiUrl = (import.meta.server) ? config.privateApiBaseUrl : config.public.apiBaseUrl
  const baseURL = (!frontendApi) ? apiUrl : '/api'

  const options: any = {
    baseURL,
    method: 'GET',
    redirect: 'follow', // this is the default
    // DO NOT USE credentials (unless you are aware of the fact that this causes caching issues due to browser cookies)
    // credentials: 'include',
    cache: 'no-store',
    headers: { ...extraHeaders },
    server
  }

  // if ($preview) {
  //   options.mode = 'no-cors'
  // }

  return useAsyncData(`apiGet_${url}`, () => {
    /*
          All request can be done:
          - server side (when directly loading a page)
          - client side (when navigating in the client)
        */
    if (import.meta.dev) {
      const executedBy = import.meta.client ? 'client' : 'server'
      console.info(`ℹ (${executedBy}) URL: ${baseURL}${url}`)
    }

    return $fetch(`${baseURL}${url}`, options)
  })
}

// Default POST method
const useAPIPost = (
  url: string,
  data: object,
  extraHeaders: any = null,
  server = true
): Promise<TypedInternalResponse<string, _AsyncData<any, Error | null>, ExtractedRouteMethod<string, any>>> => {
  const options: any = {
    method: 'POST',
    body: data,
    // DO NOT USE credentials (unless you are aware of the fact that this causes caching issues due to browser cookies)
    // credentials: 'include',
    cache: 'no-store',
    mode: 'no-cors',
    headers: { ...extraHeaders },
    server
  }

  return useAsyncData(`apiPost_${url}`, () => {
    /*
          All request can be done:
          - server side (when directly loading a page)
          - client side (when navigating in the client)
        */
    if (import.meta.dev) {
      const executedBy = import.meta.client ? 'client' : 'server'
      console.info(`ℹ (${executedBy}) URL: ${url}`)
    }

    return $fetch(url, options)
  })
}

const useAPIPatch = (
  url: string,
  data: object,
  extraHeaders: any = null,
  server = true,
  frontendApi = false
): Promise<TypedInternalResponse<string, _AsyncData<any, Error | null>, ExtractedRouteMethod<string, any>>> => {
  const config = useRuntimeConfig()

  // frontend API is a nuxt3 server endpoint.
  const apiUrl = (import.meta.server) ? config.privateApiBaseUrl : config.public.apiBaseUrl
  const baseURL = (!frontendApi) ? apiUrl : '/api'

  const options: any = {
    baseURL,
    method: 'PATCH',
    body: data,
    // DO NOT USE credentials (unless you are aware of the fact that this causes caching issues due to browser cookies)
    // credentials: 'include',
    cache: 'no-store',
    headers: { ...extraHeaders },
    server
  }

  return useAsyncData(`apiPatch_${url}`, () => {
    /*
          All request can be done:
          - server side (when directly loading a page)
          - client side (when navigating in the client)
        */

    if (import.meta.dev) {
      const executedBy = import.meta.client ? 'client' : 'server'
      console.info(`ℹ (${executedBy}) URL: ${baseURL}${url}`)
    }

    return $fetch(`${baseURL}${url}`, options)
  })
}
